import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast, useToast } from '@/hooks/use-toast';
import { HBoxCall } from "hbox-call";
import { GRAPHQL_ENDPOINT, TWILIO_TOKEN_ENDPOINT, TWILIO_WARM_TRANSFER_ENDPOINT, TWILIO_BLIND_TRANSFER_ENDPOINT, DEVICE_OPTIONS, PEP_V2_API_BASE } from '../constants';
import { getCurrentEnvironment, API_CONFIG } from '@/config/envConfig';
import { esPatientTransferService } from '@/services';

// Global state for HBox call initialization
let isHBoxCallInitialized = false;
let isInitializing = false; // Prevents race condition during async initialization
let currentSessionUserId: string | null = null;
let globalHboxCallInstance: HBoxCall | null = null;
let allowedTransferFromRoleTypes: string[] = [];

// Export cleanup function for logout
export const cleanupHBoxCall = () => {
  // Destroy the global instance if it exists
  if (globalHboxCallInstance) {
    try {
      globalHboxCallInstance.destroy();
    } catch (error) {
    }
  }

  // Reset the global flags and instance
  isHBoxCallInitialized = false;
  isInitializing = false;
  currentSessionUserId = null;
  globalHboxCallInstance = null;
};

// Global function to fetch allowed user roles for call transfer
const fetchAllowedUserRolesGlobal = async (hasuraToken: string, excludeUserId?: string) => {
  if (!hasuraToken) return [];

  try {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'authorization': `Bearer ${hasuraToken}`,
        'content-type': 'application/json; charset=utf-8',
  'origin': API_CONFIG.ORIGIN_URL,
  'referer': API_CONFIG.REFERER_URL,
      },
      body: JSON.stringify({
        query: `query getAllowedUserRoleForCallTransfer{
            reference_origin_number_type(where: {origin_number_type: {_eq: "Enrollment"}}){ 
              transferred_to_role_type, 
              transferred_from_role_type 
            }
          }`
      }),
    });

    const data = await response.json();
    if (data.data?.reference_origin_number_type?.[0]) {
      const { transferred_to_role_type, transferred_from_role_type } = data.data.reference_origin_number_type[0];
      const parsedTransferredToRoles = transferred_to_role_type?.length ? transferred_to_role_type.split(',') : [];
      allowedTransferFromRoleTypes = transferred_from_role_type?.length ? transferred_from_role_type.split(',') : [];
      
      // Always include CC, TS, NU, DO, and MO role types along with dynamically fetched roles
      const roleTypesWithCC = [...new Set([...parsedTransferredToRoles, 'CC', 'TS', 'NU', 'DO', 'MO'])];
      
      if (roleTypesWithCC && roleTypesWithCC.length > 0) {
        return await fetchUsersForCallTransferGlobal(roleTypesWithCC, hasuraToken, excludeUserId);
      }
    }
  } catch (error) {
    console.error('Error fetching allowed user roles:', error);
  }
  return [];
};

// Global function to fetch users for call transfer with optional ID filtering
const fetchUsersForCallTransferGlobal = async (
  roleTypes: string[], 
  hasuraToken: string, 
  excludeUserId?: string,
  filterByIds?: number[]
) => {
  if (!hasuraToken) return [];

  try {
    // Build where clause with optional ID filtering
    const whereClause = filterByIds && filterByIds.length > 0
      ? { type: { _in: roleTypes }, is_active: { _eq: true }, id: { _in: filterByIds } }
      : { type: { _in: roleTypes }, is_active: { _eq: true } };

    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'authorization': `Bearer ${hasuraToken}`,
        'content-type': 'application/json; charset=utf-8',
        'origin': API_CONFIG.ORIGIN_URL,
        'referer': API_CONFIG.REFERER_URL,
      },
      body: JSON.stringify({
        query: `query getUsersForCallTransfer($whereClause: api_hboxuser_bool_exp!) {
            api_hboxuser(where: $whereClause) {
              id
              first_name
              last_name
              email
              type
            }
          }`,
        variables: {
          whereClause
        }
      }),
    });

    const data = await response.json();
    if (data.data?.api_hboxuser) {
      const users = data.data.api_hboxuser
        .filter((user: any) => !excludeUserId || user.id.toString() !== excludeUserId)
        .map((user: any) => ({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          type: user.type || '',
          id: user.id.toString()
        }));

      // If filtering by IDs and no users found, add "Portal User" placeholder
      if (filterByIds && filterByIds.length > 0 && users.length === 0) {
        return filterByIds.map(id => ({
          firstName: 'Portal',
          lastName: 'User',
          email: '',
          type: '',
          id: id.toString()
        }));
      }

      return users;
    }
  } catch (error) {
    console.error('Error fetching transfer users:', error);
  }
  return [];
};

// Global initialization function that can be called from anywhere (e.g., AuthContext)
export const initializeHBoxCallGlobally = async (
  user: any,
  token: string,
  hasuraToken: string
) => {
  // Check if auth data is available
  if (!user || !token || !hasuraToken) {
    return;
  }

  // Check if already initialized or currently initializing for current session
  if ((isHBoxCallInitialized || isInitializing) && currentSessionUserId === user.id) {
    return;
  }

  // Set initializing flag immediately to prevent race condition
  isInitializing = true;
  currentSessionUserId = user.id;

  try {
    const currentEnv = getCurrentEnvironment();
    const hboxCallInstance = new HBoxCall();

    // Configure GraphQL endpoint with hasuraToken
    hboxCallInstance.configureGraphQLEndpoint(GRAPHQL_ENDPOINT, hasuraToken);

    // Get Twilio token using the API client with regular token
    // const twilioTokenResponse = await ApiClient.getTwilioToken(user.email);

    // Initialize the device

    await hboxCallInstance.initializeDevice(
      TWILIO_TOKEN_ENDPOINT,
      user.email,
      user.id,
      DEVICE_OPTIONS,
      token,
      true,
      TWILIO_WARM_TRANSFER_ENDPOINT,
      TWILIO_BLIND_TRANSFER_ENDPOINT,
      currentEnv === 'production' ? 'prod' : 'dev',
      API_CONFIG.API_BASE,
      'light',
      PEP_V2_API_BASE
    );

    const transferUsers = await fetchAllowedUserRolesGlobal(hasuraToken, user?.id?.toString())
    hboxCallInstance.updateTransferUserList(transferUsers)
    const canTransferCall = !!allowedTransferFromRoleTypes.includes(user.type);
    hboxCallInstance.setEnableTransfer(canTransferCall)

    // Only set flags after successful initialization
    globalHboxCallInstance = hboxCallInstance;
    isHBoxCallInitialized = true;
    isInitializing = false;

    // Add the event listeners but use a different approach for navigation
    if (typeof globalHboxCallInstance.addCallEventListeners === 'function') {
      globalHboxCallInstance.addCallEventListeners({
        onViewProfile: (userId: any) => {
          if (userId && !isNaN(userId)) {
            // Dispatch a custom event that can be listened to by other components
            // This prevents having to navigate directly from here, which could disrupt the call
            const customEvent = new CustomEvent('hboxViewProfile', {
              detail: {
                patientId: userId,
                fromGlobalSearch: true,
                isGlobal: true
              }
            });

            // Dispatch on window so it can be listened to anywhere in the application
            window.dispatchEvent(customEvent);
          } else {
            // Silently handle invalid patient ID
          }
        },
        onCompleteTransfer: async (transferFrom, patientId, transferTo) => {
          try {
            if (!patientId || !transferTo) {
              console.error('Missing required parameters for transfer:', { patientId, transferTo });
              return;
            }

            console.log('🔄 Transferring patient:', { transferFrom, patientId, transferTo });

            // Update service context with current auth tokens
            esPatientTransferService.updateContext({
              userId: user.id,
              token: token,
              hasuraToken: hasuraToken
            });

            // Call the service method
            const result = await esPatientTransferService.transferPatientOnCall(
              user.id,
              patientId,
              transferTo
            );

            if (result.success) {
              console.log('✅ Patient transfer completed successfully');
              toast({
                title: "Transfer Completed",
                description: result.message || `Patient has been successfully transferred.`,
              });
            } else {
              throw new Error(result.message || 'Transfer failed');
            }

          } catch (error) {
            console.error('❌ Error transferring patient:', error);
            toast({
              title: "Transfer Failed",
              description: error instanceof Error ? error.message : `Failed to transfer patient. Please try again.`,
              variant: "destructive",
            });
          }
        }
      });
    } else {

    }

  } catch (error) {
    // Reset flags on failure to allow retry
    // Clean up any partial initialization
    if (globalHboxCallInstance) {
      try {
        globalHboxCallInstance.destroy();
      } catch (cleanupError) {
        // Silent error handling
      }
    }

    isHBoxCallInitialized = false;
    isInitializing = false;
    currentSessionUserId = null;
    globalHboxCallInstance = null;

    // Re-throw the error to be handled by the caller
    throw error;
  }
};

interface CCUser {
  firstName: string;
  lastName: string;
  email: string;
  id: string;
  type: string;
}

export const useHBoxCall = () => {
  const [hboxCall, setHboxCall] = useState<HBoxCall | null>(null);
  const [canTransfer, setCanTransfer] = useState<boolean>(false);
  const [pendingUserList, setPendingUserList] = useState<CCUser[]>([]);
  const { user, token, hasuraToken } = useAuth();
  const { toast } = useToast();

  // Debug exposures removed

  // Restore global instance if already initialized (AuthContext handles initialization)
  useEffect(() => {
    const checkForGlobalInstance = () => {
      if (user && globalHboxCallInstance && isHBoxCallInitialized && currentSessionUserId === user.id) {
        setHboxCall(globalHboxCallInstance);
        return true; // Found and restored
      }
      return false; // Not found yet
    };

    // Immediately check
    if (checkForGlobalInstance()) {
      return;
    }

    // If not found, set up polling to check for global instance
    if (user) {
      const interval = setInterval(() => {
        if (checkForGlobalInstance()) {
          clearInterval(interval);
        }
      }, 100); // Check every 100ms

      // Cleanup interval after 10 seconds to avoid infinite polling
      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [user]);

  // Separate useEffect to handle updateTransferUserList when both hboxCall and pendingUserList are available
  useEffect(() => {
    if (hboxCall && pendingUserList.length > 0) {
      // hboxCall.updateTransferUserList(pendingUserList); // Method doesn't exist, commented out
      // Clear pending list after successful update
      setPendingUserList([]);
    }
  }, [hboxCall, pendingUserList]);

  return { hboxCall, canTransfer };
};